/**
 * URL state of the blog list (`/blog`, `/<blog-category>`): `?search=` (results replace the grid),
 * `?sort=most-read` (Najčítanejšie; Najnovšie is the default) and `?page=N` – the first N pages of
 * cards, so „Zobraziť ďalšie články“ is a plain crawlable link that appends the next page. The
 * default state is the clean URL (the ISR page). Safe on server and client.
 */

/** Cards per page: Figma shows two rows of three around the savings calculator. */
export const BLOG_PAGE_SIZE = 6;
/** A typed `?page=` never asks the API for more than this many pages in one request. */
export const BLOG_MAX_PAGES = 50;

export type BlogSort = 'newest' | 'most-read';
export type BlogListState = { search: string; sort: BlogSort; pages: number };

type RawSearchParams = Record<string, string | string[] | undefined> | undefined;

const first = (value: string | string[] | undefined) => (Array.isArray(value) ? value[0] : value);

export function readBlogListState(params: RawSearchParams): BlogListState {
  const page = Number(first(params?.page));
  return {
    search: (first(params?.search) ?? '').trim().slice(0, 100),
    sort: first(params?.sort) === 'most-read' ? 'most-read' : 'newest',
    pages: Number.isSafeInteger(page) && page > 1 ? Math.min(page, BLOG_MAX_PAGES) : 1,
  };
}

/** URL of the list in a state; default values stay out of the query. */
export function blogListHref(path: string, state: Partial<BlogListState>) {
  const query = new URLSearchParams();
  if (state.search) query.set('search', state.search);
  if (state.sort === 'most-read') query.set('sort', state.sort);
  if (state.pages && state.pages > 1) query.set('page', String(state.pages));
  const search = query.toString();
  return search ? `${path}?${search}` : path;
}
